/**
 * Reads the `tip-config` Flagsmith flag to resolve per-region tip defaults.
 *
 * Flagsmith flag: `tip-config` (JSON string)
 * Expected shape:
 *   {
 *     "SA": { "defaultTipPercentage": 0, "mostCommonTipPercentage": 7 },
 *     "AE": { "defaultTipPercentage": 7, "mostCommonTipPercentage": 7 },
 *     "default": { "defaultTipPercentage": 7, "mostCommonTipPercentage": 7 }
 *   }
 *
 * Fallback chain: country key → "default" key → hardcoded { 7, 7 }.
 */
import { useMemo } from 'react';
import { useCurrency } from '@/context/CurrencyContext/CurrencyContext';
import { useFeatureFlagValue } from './useFeatureFlag';

interface TipRegionConfig {
  defaultTipPercentage: number;
  mostCommonTipPercentage: number;
}

type TipConfigMap = Record<string, TipRegionConfig>;

const HARDCODED_DEFAULTS: TipRegionConfig = {
  defaultTipPercentage: 7,
  mostCommonTipPercentage: 7,
};

function isRegionConfig(value: unknown): value is TipRegionConfig {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).defaultTipPercentage === 'number' &&
    typeof (value as Record<string, unknown>).mostCommonTipPercentage === 'number'
  );
}

function parseTipConfigMap(raw: unknown): TipConfigMap | null {
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw !== 'object' || raw === null) return null;

  const map = raw as Record<string, unknown>;
  if (!('default' in map) || !isRegionConfig(map.default)) return null;

  return map as TipConfigMap;
}

export const useTipConfig = (): TipRegionConfig & { isLoading: boolean } => {
  const flagValue = useFeatureFlagValue('tip-config');
  const { country } = useCurrency();

  return useMemo(() => {
    const configMap = parseTipConfigMap(flagValue);
    if (!configMap) {
      return { ...HARDCODED_DEFAULTS, isLoading: flagValue === null };
    }

    const regionKey = country?.toUpperCase();
    const regionConfig = (regionKey && configMap[regionKey]) || configMap.default;
    return { ...regionConfig, isLoading: false };
  }, [flagValue, country]);
};
